import { useEffect, useRef, useState } from 'react'
import batchedUpdates from 'lib/batched-updates'

export function getScrollTop() {
  return document.body.scrollTop || document.documentElement.scrollTop
}

export function setScrollTop(scrollTop: number) {
  document.body.scrollTop = scrollTop
  document.documentElement.scrollTop = scrollTop
}

let isObservePaused: boolean
let needImmediateScroll: boolean

export function scrollToWithStickyObserve(scrollTop: number) {
  isObservePaused = true
  needImmediateScroll = true
  setScrollTop(scrollTop)
  setTimeout(() => {
    isObservePaused = false
  }, 500)
}

type Subscriber = (params: {
  isDown: boolean
  distance: number
  force: boolean
}) => void
const subscribers = new Set<Subscriber>()
let isListening = false
let distance = 0
let curDirection: boolean
let preTop: number

const handleScroll = () => {
  const curTop = getScrollTop()
  const curOffset = curTop - preTop
  preTop = curTop

  if (isObservePaused) {
    return
  }

  const curIsDown = curOffset > 0
  if (curIsDown !== curDirection) {
    distance = curOffset
    curDirection = curIsDown
  } else {
    distance += curOffset
  }
  const force = needImmediateScroll
  subscribers.forEach((subscriber) => {
    batchedUpdates(() => {
      subscriber({
        isDown: curIsDown,
        distance: Math.abs(distance),
        force,
      })
    })
  })
  needImmediateScroll = false
}

export function addScrollDirectionListener(subscriber: Subscriber) {
  subscribers.add(subscriber)
  if (isListening) {
    return
  }
  preTop = getScrollTop()
  distance = 0
  window.addEventListener('scroll', handleScroll)
  isListening = true
}

export function removeScrollDirectionListener(subscriber: Subscriber) {
  subscribers.delete(subscriber)
  if (subscribers.size === 0) {
    window.removeEventListener('scroll', handleScroll)
    isListening = false
  }
}

export interface IScrollStickyState {
  isFixed: boolean
  duration: number
}

export interface IScrollStickyObserveParams {
  state: IScrollStickyState
  setState(state: IScrollStickyState): void
  canListenScroll(params: { isIntersecting: boolean; top: number }): boolean
  isCurrentFixed(params: {
    isIntersecting: boolean
    prevIsIntersecting: boolean
    isScrollUp: boolean
    top: number
  }): boolean
}

function useScrollStickyObserve({
  state,
  setState,
  canListenScroll,
  isCurrentFixed,
}: IScrollStickyObserveParams) {
  const [markNode, setMarkNode] = useState<HTMLElement | null>(null)

  useEffect(() => {
    return () => {
      setState({
        isFixed: false,
        duration: 0,
      })
    }
  }, [])

  const [isIntersecting, setIsIntersecting] = useState(false)
  const prevIsIntersectingRef = useRef(isIntersecting)
  const [top, setTop] = useState(0)
  const [isScrollUp, setIsScrollUp] = useState(false)
  const isFixedRef = useRef(state.isFixed)

  useEffect(() => {
    const subscriber: Subscriber = ({ isDown, distance, force }) => {
      if (force || (distance >= 120 && isScrollUp !== isDown)) {
        setIsScrollUp(isDown)
      }
    }
    if (canListenScroll({ isIntersecting, top }) || isFixedRef.current) {
      addScrollDirectionListener(subscriber)
    }
    return () => {
      removeScrollDirectionListener(subscriber)
    }
  }, [isIntersecting, canListenScroll, top, isScrollUp])

  useEffect(() => {
    const isFixed = isCurrentFixed({
      isIntersecting,
      prevIsIntersecting: prevIsIntersectingRef.current,
      isScrollUp,
      top,
    })
    prevIsIntersectingRef.current = isIntersecting
    if (isFixed === isFixedRef.current) {
      return
    }
    isFixedRef.current = isFixed
    setState({
      isFixed,
      duration: 300,
    })
  }, [isIntersecting, isScrollUp, top])

  useEffect(() => {
    if (!markNode) return
    const observer = new IntersectionObserver((changes) => {
      changes.forEach((change) => {
        if (isObservePaused) {
          return
        }
        const { top } = change.boundingClientRect
        batchedUpdates(() => {
          setIsIntersecting(change.isIntersecting)
          setTop(top)
        })
      })
    }, {})
    observer.observe(markNode)

    return () => {
      observer.disconnect()
    }
  }, [markNode])

  return { state, setMarkNode }
}

export default useScrollStickyObserve
